/*
 * handlebars-lite — a ~2KB drop-in replacement for the Handlebars runtime,
 * supporting only the subset of features used by this theme's templates:
 *   {{var}} {{{raw}}} {{&raw}}  dotted paths, this, ../parent, @key/@index/@first/@last/@root
 *   {{#if}}{{else}}{{/if}}  {{#unless}}  {{#each x}}  {{#with x}}  {{^x}}
 *   {{#section}} (Mustache/blockHelperMissing: array iterates, object => with, falsy => inverse)
 *   {{! comments }}
 * Exposes window.Handlebars.compile(src) => fn(data) => html, matching Handlebars semantics
 * (isEmpty truthiness, HTML escaping) so existing templates + call sites work unchanged.
 */
(function (global) {
  'use strict';

  var escapeMap = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#x27;', '`': '&#x60;', '=': '&#x3D;' };
  var escapeRe = /[&<>"'`=]/g;
  function escapeExpression(value) {
    if (value && typeof value.toHTML === 'function') return value.toHTML();
    if (!value && value !== 0) return '';
    value = '' + value;
    if (!escapeRe.test(value)) return value;
    return value.replace(escapeRe, function (c) { return escapeMap[c]; });
  }

  // Handlebars Utils.isEmpty: falsy (but 0 is NOT empty) or empty array.
  // Used by sections/with (0 is truthy => renders once).
  function isEmpty(v) {
    if (!v && v !== 0) return true;
    if (Array.isArray(v) && v.length === 0) return true;
    return false;
  }

  // Handlebars {{#if}}/{{#unless}} default (no includeZero): plain JS-falsy
  // OR empty array is falsy — so 0 and '' are FALSY, but {} is truthy.
  function ifFalsy(v) {
    if (Array.isArray(v)) return v.length === 0;
    return !v;
  }

  function tokenize(src) {
    var tokens = [], re = /\{\{\{([\s\S]*?)\}\}\}|\{\{([\s\S]*?)\}\}/g, last = 0, m;
    while ((m = re.exec(src))) {
      if (m.index > last) tokens.push({ t: 'text', v: src.slice(last, m.index) });
      if (m[1] != null) tokens.push({ t: 'mus', raw: true, e: m[1] });
      else tokens.push({ t: 'mus', raw: false, e: m[2] });
      last = re.lastIndex;
    }
    if (last < src.length) tokens.push({ t: 'text', v: src.slice(last) });
    return tokens;
  }

  function addChild(parent, node) {
    if (parent._inv) parent.inverse.push(node);
    else parent.children.push(node);
  }

  function parse(src) {
    var root = { type: 'root', children: [] };
    var stack = [root];
    tokenize(src).forEach(function (tk) {
      var top = stack[stack.length - 1];
      if (tk.t === 'text') { addChild(top, { type: 'text', v: tk.v }); return; }
      var e = tk.e.trim();
      var c = e.charAt(0);
      if (c === '!' || c === '>') return;                 // comment / partial: ignore
      if (c === '#' || c === '^') {
        var parts = e.slice(1).trim().split(/\s+/);
        var node = { type: 'block', name: parts[0], args: parts.slice(1), inverted: c === '^', children: [], inverse: [], _inv: false };
        addChild(top, node);
        stack.push(node);
        return;
      }
      if (c === '/') { stack.pop(); return; }
      if (e === 'else') { top._inv = true; return; }
      var raw = tk.raw || c === '&';
      addChild(top, { type: 'var', e: c === '&' ? e.slice(1).trim() : e, raw: raw });
    });
    return root;
  }

  function resolve(expr, frame) {
    expr = expr.trim();
    if (expr.charAt(0) === '@') {
      var k = expr.slice(1);
      if (k === 'root') return frame.root;
      return frame.data ? frame.data[k] : undefined;
    }
    var f = frame;
    while (expr.slice(0, 3) === '../') { expr = expr.slice(3); f = f.parent || f; }
    var base = f.ctx;
    if (expr === 'this' || expr === '.' || expr === '') return base;
    if (expr.slice(0, 5) === 'this.') expr = expr.slice(5);
    var segs = expr.split('.'), val = base;
    for (var i = 0; i < segs.length; i++) {
      if (val == null) return undefined;
      val = val[segs[i]];
    }
    return val;
  }

  function frameFor(ctx, parent, data) {
    return { ctx: ctx, data: data || (parent ? parent.data : {}), parent: parent, root: parent ? parent.root : ctx };
  }

  function renderEach(v, node, frame) {
    if (isEmpty(v)) return renderList(node.inverse, frame);
    var out = '', i, keys;
    if (Array.isArray(v)) {
      for (i = 0; i < v.length; i++) {
        out += renderList(node.children, frameFor(v[i], frame, { index: i, key: i, first: i === 0, last: i === v.length - 1 }));
      }
    } else if (typeof v === 'object') {
      keys = Object.keys(v);
      for (i = 0; i < keys.length; i++) {
        out += renderList(node.children, frameFor(v[keys[i]], frame, { key: keys[i], index: i, first: i === 0, last: i === keys.length - 1 }));
      }
    }
    return out;
  }

  function renderBlock(node, frame) {
    var name = node.name;
    var val;
    if (node.inverted) {                                  // {{^x}} renders when x is empty
      val = resolve(name, frame);
      return isEmpty(val) ? renderList(node.children, frame) : renderList(node.inverse, frame);
    }
    if (name === 'if') {
      val = resolve(node.args[0], frame);
      return !ifFalsy(val) ? renderList(node.children, frame) : renderList(node.inverse, frame);
    }
    if (name === 'unless') {
      val = resolve(node.args[0], frame);
      return ifFalsy(val) ? renderList(node.children, frame) : renderList(node.inverse, frame);
    }
    if (name === 'with') {
      val = resolve(node.args[0], frame);
      return !isEmpty(val) ? renderList(node.children, frameFor(val, frame)) : renderList(node.inverse, frame);
    }
    if (name === 'each') {
      return renderEach(resolve(node.args[0], frame), node, frame);
    }
    // blockHelperMissing: {{#section}} where section is a data value
    val = resolve(name, frame);
    if (typeof val === 'function') val = val();
    if (val === true) return renderList(node.children, frame);
    if (val === false || val == null) return renderList(node.inverse, frame);
    if (Array.isArray(val)) return val.length ? renderEach(val, node, frame) : renderList(node.inverse, frame);
    return renderList(node.children, frameFor(val, frame));   // object/scalar => render once with it as context
  }

  function renderList(nodes, frame) {
    var out = '';
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.type === 'text') { out += n.v; continue; }
      if (n.type === 'block') { out += renderBlock(n, frame); continue; }
      var v = resolve(n.e, frame);
      if (typeof v === 'function') v = v();
      if (v == null) continue;
      out += n.raw ? (v && typeof v.toHTML === 'function' ? v.toHTML() : '' + v) : escapeExpression(v);
    }
    return out;
  }

  function SafeString(s) { this.string = s; }
  SafeString.prototype.toString = SafeString.prototype.toHTML = function () { return '' + this.string; };

  var Handlebars = {
    compile: function (src) {
      var tree = parse('' + src);
      return function (data) { return renderList(tree.children, frameFor(data, null, {})); };
    },
    precompile: function (src) { return Handlebars.compile(src); },
    registerHelper: function () {},
    registerPartial: function () {},
    escapeExpression: escapeExpression,
    Utils: { escapeExpression: escapeExpression, isEmpty: isEmpty },
    SafeString: SafeString
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = Handlebars;
  global.Handlebars = Handlebars;
})(typeof window !== 'undefined' ? window : this);
